// DiagnoseVerdictCard — presentational read-only renderer for an AI weak-point
// diagnosis.
//
// 19BO+ Line 2 B4.3 · §12.2 铁律 4 (diagnose 输出是只读 verdict · 不包 EditableItem).
//
// Sibling to EvaluateVerdictCard (NOT a prop flag — see evaluate-verdict-card.tsx
// header comment line 17-19). They share the meta + markdown rendering pattern but
// render fundamentally different content:
//   evaluate: a single score + rubric dimensions + feedback
//   diagnose: multiple weaknesses (severity-ranked) + cross-weakness summary + practice
//
// Composition stance — mirrors EvaluateVerdictCard / MCQCard:
//   The card knows nothing about persistence (where the verdict came from / is stored),
//   re-diagnosing, or which view hosts it. Callers inject a `slot` for view-specific
//   controls (e.g. a "re-diagnose" button on course-dashboard). statsSnapshot, when
//   provided, renders a freshness hint ("基于 N 道错题") so users know how stale the
//   diagnosis is — useful because a diagnosis is a snapshot, not a permanent grade.
//
// Why presentational (no fetch/state):
//   The verdict lifecycle (trigger / load / persist) lives in the host view +
//   DiagnoseDialog. This card is the post-diagnosis display — pure props in, JSX out.

import { type ReactNode } from 'react';
import type { DiagnoseVerdict, DiagnosisStatsSnapshot } from '@examforge/shared/schemas';
import { MarkdownBlock } from '../markdown-block.js';
import { useT } from '../../i18n/use-t.js';

export interface DiagnoseVerdictCardProps {
  /** The AI diagnosis verdict to render (weaknesses / summary / practice / meta). */
  verdict: DiagnoseVerdict;
  /** Optional snapshot of the wrong-answer counts the diagnosis was based on.
   *  When provided, renders a freshness hint. Omit to suppress. */
  statsSnapshot?: DiagnosisStatsSnapshot;
  /** Inject view-specific controls below the verdict (e.g. re-diagnose button).
   *  The card is unaware of what goes here. */
  slot?: ReactNode;
  /** Optional extra class on the root <article>. Combines with the base class. */
  className?: string;
}

const SEVERITY_KEY = {
  high: 'diagnose.severityHigh',
  medium: 'diagnose.severityMedium',
  low: 'diagnose.severityLow',
} as const;

export function DiagnoseVerdictCard({
  verdict,
  statsSnapshot,
  slot,
  className,
}: DiagnoseVerdictCardProps): ReactNode {
  const t = useT();
  const { weaknesses, overallSummary, suggestedPractice, diagnosedAt, modelVersion } = verdict;
  // Locale-neutral date rendering — use the ISO string's date portion (mirrors
  // evaluate-verdict-card). The host view may pass a localized string via slot.
  const dateLabel = diagnosedAt.slice(0, 10);
  // {total} placeholder interpolated by useT's params (no more bare .replace).
  const statsBasisLabel = statsSnapshot
    ? t('diagnose.statsBasisLabel', { total: statsSnapshot.total })
    : null;

  return (
    <article
      className={`diagnose-verdict-card${className ? ` ${className}` : ''}`}
      data-testid="diagnose-verdict-card"
    >
      <div className="diagnose-weaknesses">
        <span className="diagnose-section-label">{t('diagnose.weaknessesLabel')}</span>
        <ul className="diagnose-weakness-list">
          {weaknesses.map((w, i) => (
            <li
              key={`${w.attribution}-${w.target}-${i}`}
              className={`diagnose-weakness diagnose-weakness--${w.severity}`}
            >
              <div className="diagnose-weakness-head">
                <span className="diagnose-weakness-target">{w.target}</span>
                <span className={`diagnose-severity-badge diagnose-severity-${w.severity}`}>
                  {t(SEVERITY_KEY[w.severity])}
                </span>
              </div>
              <p className="diagnose-weakness-evidence">{w.evidence}</p>
              <div className="diagnose-weakness-recommendation markdown-body">
                <MarkdownBlock>{w.recommendation}</MarkdownBlock>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="diagnose-summary markdown-body">
        <span className="diagnose-section-label">{t('diagnose.summaryLabel')}</span>
        <MarkdownBlock>{overallSummary}</MarkdownBlock>
      </div>

      <div className="diagnose-practice markdown-body">
        <span className="diagnose-section-label">{t('diagnose.practiceLabel')}</span>
        <MarkdownBlock>{suggestedPractice}</MarkdownBlock>
      </div>

      <p className="diagnose-verdict-meta">
        <span>{modelVersion}</span>
        <span aria-hidden="true"> · </span>
        <span>{dateLabel}</span>
        {statsBasisLabel ? (
          <>
            <span aria-hidden="true"> · </span>
            <span data-testid="diagnose-stats-basis">{statsBasisLabel}</span>
          </>
        ) : null}
      </p>

      {slot ? <div className="diagnose-verdict-slot">{slot}</div> : null}
    </article>
  );
}
